import React from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import { Box, Grid, TextField, Typography } from "@material-ui/core";
import styled from "styled-components";
import InputMask from "react-input-mask";
import _ from "lodash";
import { withAlert } from "react-alert";
import { compose } from "redux";
import WuiButton from "../../../waybee-ui/Button";
import Dialog from "../../../waybee-ui/Dialog";
import DialogTitle from "../../../waybee-ui/Dialog/DialogTitle";
import DialogContent from "../../../waybee-ui/Dialog/DialogContent";
import DialogActions from "../../../waybee-ui/Dialog/DialogActions";
import { validateCpf, normalizeCpf } from "../../../utils/validateCpf";

const Button = styled(WuiButton)`
  min-width: 300px;
`;

const DialogEdit = ({ open, onClose, onSend, user, alert }) => {
  const {
    register,
    getValues,
    control,
    formState: { errors },
    reset,
    watch
  } = useForm({
    defaultValues: {
      ...user
    }
  });

  const formValues = watch();

  const hasChanges = React.useMemo(() => {
    const currentValues = {
      ...formValues,
      documentNumber: normalizeCpf(formValues.documentNumber)
    };

    const originalValues = {
      ...user,
      documentNumber: normalizeCpf(user.documentNumber)
    };

    return !_.isEqual(currentValues, originalValues);
  }, [formValues, user]);

  React.useEffect(() => {
    if (open) {
      reset(user);
    }
  }, [open, user, reset]);

  const handleClose = () => {
    reset(user);
    onClose();
  };

  const handleSend = () => {
    if (!hasChanges) {
      alert.show("Nenhuma alteração foi feita nos dados", { title: "Aviso" });
      return;
    }

    const values = getValues();
    values.documentNumber = normalizeCpf(values.documentNumber);
    const isValidCpf = validateCpf(values.documentNumber);

    if (!isValidCpf) {
      alert.show("CPF inválido", { title: "Erro" });
      return;
    }

    onSend(values);
  };

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      maxWidth="sm"
      fullWidth
      closeButton
    >
      <DialogTitle onClose={handleClose}>Editar detalhes</DialogTitle>

      <DialogContent>
        <Box mt={1} mb={3}>
          <Typography variant="subtitle1">
            Altere os detalhes do cadastro do responsável e clique em Salvar.
          </Typography>
        </Box>

        <Box mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField {...register("name")} label="Nome no app" fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register("email")}
                label="E-mail cadastrado no app"
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="documentNumber"
                control={control}
                render={({ field }) => (
                  <InputMask {...field} mask="999.999.999-99">
                    {inputProps => (
                      <TextField
                        {...inputProps}
                        label="CPF"
                        fullWidth
                        error={!!errors.documentNumber}
                        helperText={errors.documentNumber?.message}
                      />
                    )}
                  </InputMask>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                {...register("phone")}
                label="Número de contato"
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          autoFocus
          onClick={handleSend}
          color="primary"
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DialogEdit.defaultProps = {
  user: {}
};

DialogEdit.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  user: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    documentNumber: PropTypes.string,
    phone: PropTypes.string
  }),
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired
};

export default compose(withAlert())(DialogEdit);
